import jsPDF from 'jspdf';
import { obtenerLetras } from './PlanillaTemplate';
import EscudoAlcaldiaBlanco from '../../../assets/landing/medellin.png';
import { PDF_PW, PDF_PH, PDF_MG, PDF_CW } from '../../../utils/pdfPage';

// Márgenes compartidos con el resto de exportaciones (30 mm, igual que los informes en Word)
const PW = PDF_PW;
const PH = PDF_PH;
const MG = PDF_MG;
const CW = PDF_CW;

let _logoCache: string | null = null;

const getLogoBase64 = async (): Promise<string> => {
  if (_logoCache) return _logoCache;
  const src = EscudoAlcaldiaBlanco as unknown as string;
  if (src.startsWith('data:')) {
    _logoCache = src;
    return _logoCache;
  }
  const blob = await fetch(src).then(r => r.blob());
  _logoCache = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  return _logoCache;
};

const checkPage = (pdf: jsPDF, y: number, needed: number): number => {
  if (y + needed > PH - MG) {
    pdf.addPage();
    return MG;
  }
  return y;
};

const imgFormat = (dataUrl: string): 'PNG' | 'JPEG' =>
  dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')
    ? 'JPEG'
    : 'PNG';

const dibujarCabecera = (pdf: jsPDF, datos: any, logoBase64: string, y: number): number => {
  const LH = 3.5;
  const PAD = 2;
  const LABEL_H = 4;

  const C1W = Math.round(CW * 0.39);
  const C2W = Math.round(CW * 0.16);
  const C3W = CW - C1W - C2W - 6;

  const str = (v: any) => (v == null ? '' : String(v));

  // Pre-calcular wrapping con font=8 para altura dinámica
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  const nombreLines = pdf.splitTextToSize(str(datos.nombre_propuesta), C1W);
  const codigoLines = pdf.splitTextToSize(str(datos.radicado), C2W);
  const catLines    = pdf.splitTextToSize(str(datos.categoria_linea_convocatoria), C3W);
  const lineaLines  = pdf.splitTextToSize(str(datos.linea_convocatoria), CW - 4);

  const maxRow1 = Math.max(nombreLines.length, codigoLines.length, catLines.length);
  const row1H = maxRow1 * LH + LABEL_H + PAD * 2;
  const row2H = lineaLines.length * LH + LABEL_H + PAD * 2;
  const TITLE_H = 21;
  const H = TITLE_H + row1H + row2H + 1;

  pdf.setFillColor(240, 240, 240);
  pdf.rect(MG, y, CW, H, 'F');

  try {
    pdf.addImage(logoBase64, imgFormat(logoBase64), MG + 2, y + 2, 18, 12);
  } catch { /* logo no disponible */ }

  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('SECRETARÍA DE CULTURA CIUDADANA', PW / 2, y + 9, { align: 'center' });
  pdf.setFontSize(8);
  pdf.text('CONVOCATORIA DE FOMENTO Y ESTÍMULOS PARA EL ARTE Y LA CULTURA', PW / 2, y + 16, { align: 'center' });

  // Fila 1: Nombre | Código | Categoría
  const ry1 = y + TITLE_H + PAD;
  const x1 = MG + 2;
  const x2 = MG + 2 + C1W + 2;
  const x3 = MG + 2 + C1W + C2W + 4;

  pdf.setFontSize(8);

  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(108, 104, 104);
  pdf.text('Nombre', x1, ry1);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(75, 75, 75);
  if (nombreLines.length) pdf.text(nombreLines, x1, ry1 + LABEL_H);

  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(108, 104, 104);
  pdf.text('Código', x2, ry1);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(75, 75, 75);
  if (codigoLines.length) pdf.text(codigoLines, x2, ry1 + LABEL_H);

  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(108, 104, 104);
  // En hojas de vida el campo es el área o expresión a la que se postuló el jurado
  pdf.text(datos.esHojaDeVida ? 'Expresión artística' : 'Categoría', x3, ry1);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(75, 75, 75);
  if (catLines.length) pdf.text(catLines, x3, ry1 + LABEL_H);

  // Fila 2: Línea (ancho completo)
  const ry2 = ry1 + row1H;

  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(108, 104, 104);
  pdf.text('Línea', x1, ry2);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(75, 75, 75);
  if (lineaLines.length) pdf.text(lineaLines, x1, ry2 + LABEL_H);

  return y + H + 1;
};

const dibujarBandaJurado = (pdf: jsPDF, jurado: any, y: number): number => {
  const H = 11;
  pdf.setFillColor(174, 62, 151);
  pdf.rect(MG, y, CW, H, 'F');

  const nombre = ['Jurado:', jurado.primer_nombre, jurado.segundo_nombre, jurado.primer_apellido, jurado.segundo_apellido]
    .filter(Boolean).join(' ').replace(/\s+/g, ' ');

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  pdf.text(nombre, MG + 3, y + 7.5);

  return y + H + 1;
};

const dibujarCriterios = (pdf: jsPDF, criterios: any[], startY: number): number => {
  const sorted = [...criterios].sort((a, b) => a.id - b.id);
  const COL_NOTA = 18;
  const COL_CONTENT = CW - COL_NOTA - 2;
  const LINE_H = 3.8;
  const MIN_ROW_H = 5;
  const PAD_V = 1;
  const GAP = 0;
  let y = startY;

  sorted.forEach((c, idx) => {
    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'bold');
    const nombreLines = pdf.splitTextToSize(c.nombre || '', COL_CONTENT - 4);
    pdf.setFont('helvetica', 'normal');
    const obsLines = pdf.splitTextToSize(c.observaciones || '', COL_CONTENT - 4);
    const rowH = Math.max(MIN_ROW_H, PAD_V + nombreLines.length * LINE_H + GAP + obsLines.length * LINE_H + PAD_V);

    y = checkPage(pdf, y, rowH);

    const bg = idx % 2 === 0 ? 240 : 255;
    pdf.setFillColor(bg, bg, bg);
    pdf.rect(MG, y, CW, rowH, 'F');

    // Col 1: nombre bold + observaciones normal
    let textY = y + PAD_V + LINE_H * 0.8;
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(nombreLines, MG + 2, textY);
    textY += nombreLines.length * LINE_H + GAP;

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    pdf.text(obsLines, MG + 2, textY);

    // Col 2: nota centrada verticalmente
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text(String(c.nota ?? ''), PW - MG - 2, y + rowH / 2 + 1.5, { align: 'right' });

    y += rowH;
  });

  return y;
};

const dibujarFooter = (
  pdf: jsPDF,
  jurado: any,
  sumatoriaTotalRedondeado: number,
  informeEvaluacion: string,
  startY: number
): void => {
  const sumatoriaNotas: number = jurado.criterios.reduce((t: number, c: any) => t + c.nota, 0);
  let y = startY + 2;

  const boxH = 15;
  y = checkPage(pdf, y, boxH);
  pdf.setFillColor(254, 244, 222);
  pdf.rect(MG, y, CW, boxH, 'F');

  pdf.setFontSize(9);
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Puntaje total:', MG + 3, y + 5);
  pdf.setFont('helvetica', 'bold');
  pdf.text(String(sumatoriaNotas), PW - MG - 2, y + 5, { align: 'right' });

  pdf.setFont('helvetica', 'normal');
  pdf.text('Puntaje total en letras: ', MG + 3, y + 11);
  pdf.setFont('helvetica', 'bold');
  pdf.text(obtenerLetras(sumatoriaNotas), MG + 3 + pdf.getTextWidth('Puntaje total en letras: '), y + 11);

  y += boxH + 3;

  if (jurado.src) {
    const FIRMA_W = 22;
    const FIRMA_H = 11;
    y = checkPage(pdf, y, FIRMA_H + 4);
    try {
      pdf.addImage(jurado.src, imgFormat(jurado.src), (PW - FIRMA_W) / 2, y, FIRMA_W, FIRMA_H);
    } catch { /* firma no disponible */ }
    y += FIRMA_H + 2;
  }

  if (informeEvaluacion) {
    y = checkPage(pdf, y, 8);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text(informeEvaluacion, PW / 2, y, { align: 'center' });
    y += 7;
  }

  y = checkPage(pdf, y, 10);
  pdf.setDrawColor(160, 160, 160);
  pdf.line(MG, y, PW - MG, y);
  y += 6;

  y = checkPage(pdf, y, 8);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(0, 0, 0);
  pdf.text(`Puntaje promedio: ${sumatoriaTotalRedondeado}`, PW - MG - 2, y, { align: 'right' });
};

export const generarPlanillaPDF = async (
  datos: any,
  juradosCriterios: any[],
  informeEvaluacion: string
): Promise<Blob> => {
  const logoBase64 = await getLogoBase64();
  const pdf = new jsPDF('p', 'mm', 'a4');

  // Líneas "SM -" (Secretaría de las Mujeres): el "Puntaje promedio" muestra la suma de los 3 jurados, sin dividir.
  const esLineaSM = String(datos?.linea_convocatoria ?? '').includes('SM -');

  const sumatoriaTotalRedondeado = parseFloat(
    juradosCriterios
      .reduce((total, j) => {
        const notasJurado = j.criterios.reduce((ct: number, c: any) => ct + c.nota, 0);
        return total + (esLineaSM ? notasJurado : notasJurado / juradosCriterios.length);
      }, 0)
      .toFixed(2)
  );

  const sortedJurados = [...juradosCriterios].sort((a, b) =>
    a.primer_nombre.localeCompare(b.primer_nombre)
  );

  sortedJurados.forEach((jurado, idx) => {
    try {
      if (idx > 0) pdf.addPage();
      let y = MG;
      y = dibujarCabecera(pdf, datos, logoBase64, y);
      y = dibujarBandaJurado(pdf, jurado, y);
      y = dibujarCriterios(pdf, jurado.criterios, y);
      dibujarFooter(pdf, jurado, sumatoriaTotalRedondeado, informeEvaluacion, y);
    } catch (err) {
      console.error(`[PDF] Error dibujando jurado ${jurado.primer_nombre} ${jurado.primer_apellido} (radicado: ${datos.radicado}):`, err);
      throw err;
    }
  });

  return pdf.output('blob');
};

// El helper de concurrencia vive en utils porque también lo usa la verificación automática.
export { runWithConcurrency } from '../../../utils/concurrencia';
